/**
 * The menu bar, as data.
 *
 * A menu item is a label, an optional shortcut to show beside it, whether it is available,
 * and what it does. Writing the menus as a list of those rather than as JSX means the bar
 * itself is twenty lines of rendering, the same item can appear in a menu and in a palette
 * without being written twice, and what the editor can do is readable in one file instead of
 * being spread across the components that happen to offer it.
 *
 * Every item that edits the document goes through `actions.edit`, which takes the command or
 * the refusal an editing layer answers with. Nothing here decides whether an edit is
 * possible: the package does that, in words, and the page shows them.
 */

import { INTERVALS, clipboard, selection as select, transpose } from "@app/score-editor";
import type { Interval } from "@app/score-editor";
import type { ScoreActions } from "./score-actions";
import { RULE } from "./score-menu-model";
import type { Menu, MenuAction } from "./score-menu-model";
import { noteMenu } from "./score-menus-music";
import { barMenu } from "./score-menus-bars";
import { instrumentMenu } from "./score-menus-instrument";
import { lineMenu } from "./score-menus-lines";
import { ornamentMenu } from "./score-menus-ornaments";
import { pageMenu } from "./score-menus-page";

/** An interval by name, so a menu item does not depend on where it sits in the list. */
export function intervalNamed(name: string): Interval {
  const found = INTERVALS.find((candidate) => candidate.name === name);
  // The names are this package's own constants; a miss would be a typo here, not a user
  // action, and a semitone is the least surprising thing to do about it.
  return found ?? INTERVALS[0];
}

const SEMITONE = intervalNamed("Minor 2nd");
const OCTAVE = intervalNamed("Octave");

function fileMenu(actions: ScoreActions): Menu {
  // One item per part rather than a submenu: a part is saved by name, and a menu with no
  // nesting in it is one anyone can read down. A score with no parts in it yet shows
  // neither the items nor the rule that would otherwise end the menu with a line.
  const parts = actions.score.parts.map(
    (part): MenuAction => ({
      label: `Save the ${part.name} part`,
      run: () => actions.savePart(part.id),
    }),
  );

  return {
    title: "File",
    items: [
      { label: "New score…", run: actions.newScore },
      { label: "Open…", hint: "⌘O", run: actions.openFile },
      RULE,
      { label: "Save", hint: "⌘S", run: actions.save },
      { label: "Export MusicXML", hint: "⌘E", run: actions.exportXml },
      { label: "Export MIDI", run: actions.exportMidi },
      RULE,
      { label: "Print…", hint: "⌘P", run: actions.print },
      ...(parts.length === 0 ? [] : [RULE, ...parts]),
    ],
  };
}

function editMenu(actions: ScoreActions): Menu {
  const { score, selection } = actions;
  const nothing = select.isEmpty(selection);
  const caret = actions.caret;

  const move = (interval: Interval, direction: 1 | -1): void => {
    actions.edit(transpose.transpose(score, selection, interval, direction));
  };

  return {
    title: "Edit",
    items: [
      {
        label: actions.undoLabel === null ? "Undo" : `Undo ${actions.undoLabel}`,
        hint: "⌘Z",
        disabled: !actions.canUndo,
        run: actions.undo,
      },
      {
        label: actions.redoLabel === null ? "Redo" : `Redo ${actions.redoLabel}`,
        hint: "⇧⌘Z",
        disabled: !actions.canRedo,
        run: actions.redo,
      },
      RULE,
      { label: "Cut", hint: "⌘X", disabled: nothing, run: actions.cut },
      { label: "Copy", hint: "⌘C", disabled: nothing, run: actions.copy },
      { label: "Paste", hint: "⌘V", disabled: !actions.hasClip, run: actions.paste },
      {
        label: "Delete",
        hint: "⌫",
        disabled: nothing,
        run: () => actions.edit(clipboard.remove(score, selection), "delete"),
      },
      RULE,
      {
        label: "Select bar",
        disabled: caret === null,
        run: () => {
          if (caret === null) return;
          actions.select(select.selectMeasure(score, caret.partId, caret.measureId));
        },
      },
      {
        label: "Select part",
        disabled: caret === null,
        run: () => {
          if (caret === null) return;
          actions.select(select.selectAll(score, caret.partId));
        },
      },
      { label: "Select all", hint: "⌘A", run: () => actions.select(select.selectAll(score)) },
      RULE,
      { label: "Up a semitone", hint: "↑", disabled: nothing, run: () => move(SEMITONE, 1) },
      { label: "Down a semitone", hint: "↓", disabled: nothing, run: () => move(SEMITONE, -1) },
      { label: "Up an octave", hint: "⇧↑", disabled: nothing, run: () => move(OCTAVE, 1) },
      { label: "Down an octave", hint: "⇧↓", disabled: nothing, run: () => move(OCTAVE, -1) },
    ],
  };
}

function playMenu(actions: ScoreActions): Menu {
  return {
    title: "Play",
    items: [
      { label: actions.playing ? "Pause" : "Play", hint: "space", run: actions.playPause },
      { label: "Stop", hint: "esc", run: actions.stop },
      RULE,
      {
        label: "Loop the selection",
        hint: "L",
        checked: actions.loop !== null,
        run: actions.toggleLoop,
      },
      { label: "Metronome", hint: "M", checked: actions.metronome, run: actions.toggleMetronome },
      { label: "Count in", checked: actions.countIn, run: actions.toggleCountIn },
      { label: "Play the marks", checked: actions.marks, run: actions.toggleMarks },
    ],
  };
}

function viewMenu(actions: ScoreActions): Menu {
  return {
    title: "View",
    items: [
      { label: "Zoom in", hint: "⌘+", run: () => actions.zoom(6) },
      { label: "Zoom out", hint: "⌘−", run: () => actions.zoom(-6) },
      { label: `Zoom: ${actions.scale}%`, disabled: true, run: () => undefined },
      RULE,
      {
        label: "Show the paper",
        checked: actions.paper,
        run: actions.togglePaper,
      },
      {
        label: "Concert pitch",
        checked: actions.concert,
        run: actions.toggleConcert,
      },
      RULE,
      {
        label: "Full screen",
        checked: actions.fullscreen,
        run: actions.toggleFullscreen,
      },
    ],
  };
}

function helpMenu(actions: ScoreActions): Menu {
  return {
    title: "Help",
    items: [{ label: "Keyboard shortcuts", hint: "?", run: actions.toggleHelp }],
  };
}

/** Every menu, in the order a bar shows them. */
export function menusFor(actions: ScoreActions): readonly Menu[] {
  return [
    fileMenu(actions),
    editMenu(actions),
    noteMenu(actions),
    ornamentMenu(actions),
    lineMenu(actions),
    barMenu(actions),
    instrumentMenu(actions),
    pageMenu(actions),
    playMenu(actions),
    viewMenu(actions),
    helpMenu(actions),
  ];
}
